import { FocusEvent, useLayoutEffect, useRef, useState } from 'react';

import type { OpsManager, OpsManagerSummary } from '@/services/opsRota/types';
import styled from '@/styles';

import { useDismiss } from '../hooks/useDismiss';
import {
  INACTIVE_CHIP_NOTE,
  MANAGERS_LOAD_ERROR,
  NO_MANAGERS,
} from '../messages';
import { C, LinkButton, PrimaryButton } from '../Operations.styles';
import { isInactiveManager } from '../scheduleForm';

import { CheckIcon, ChevronDown, XMarkIcon } from './icons';
import { FieldLabel } from './TimePicker';

// UI spec §9.3 (multi-select with a draft and "Apply selection") and §9.4
// (chips). R15 adds the empty roster and the deactivated-manager chip.

const Column = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const Wrap = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const Anchor = styled('div', { position: 'relative' });

const Trigger = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  height: '36px',
  margin: 0,
  padding: '0 8px 0 16px',
  border: 'none',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  color: C.ink,
  font: 'inherit',
  fontSize: '14px',
  textAlign: 'left',
  cursor: 'pointer',
  '& > span': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '& svg': {
    flexShrink: 0,
    width: '24px',
    height: '24px',
  },
  '&[aria-expanded="true"]': { boxShadow: `inset 0 0 0 1px ${C.green}` },
  '&[aria-expanded="true"] svg': { transform: 'rotate(180deg)' },
  '&:focus-visible': { outline: `2px solid ${C.darkGreen}` },
  '&:disabled': { cursor: 'not-allowed' },
  variants: { placeholder: { true: { '& > span': { color: C.grey } } } },
});

const Dropdown = styled('div', {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '16px',
  padding: '20px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.softBorder}, 0 2px 4px 0 rgba(0,0,0,0.08)`,
  variants: {
    layer: { panel: { zIndex: 10 }, modal: { zIndex: 60 } },
  },
  defaultVariants: { layer: 'panel' },
});

const Options = styled('div', {
  alignSelf: 'stretch',
  // A long roster scrolls inside the dropdown.
  maxHeight: '240px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const Option = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
  height: '40px',
  margin: 0,
  padding: 0,
  border: 'none',
  borderRadius: '8px',
  background: 'none',
  color: C.ink,
  font: 'inherit',
  fontSize: '14px',
  textAlign: 'left',
  cursor: 'pointer',
  '& > span:last-child': { flex: 1, minWidth: 0 },
  // A rounded ring inside the row, so the scroll box never clips it.
  '&:focus-visible': {
    outline: 'none',
    boxShadow: `inset 0 0 0 2px ${C.darkGreen}`,
  },
  // A deactivated manager can be unticked, never ticked again.
  '&[aria-disabled="true"]': { cursor: 'not-allowed' },
});

const InactiveNote = styled('span', { color: C.muted });

// A 24px box around a 20px square at (2, 2).
const Box = styled('span', {
  position: 'relative',
  flexShrink: 0,
  width: '24px',
  height: '24px',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '2px',
    borderRadius: '6px',
    background: '#fff',
    boxShadow: `inset 0 0 0 1px ${C.grey}`,
  },
  '& svg': {
    position: 'absolute',
    top: '5px',
    left: '5px',
    width: '14px',
    height: '14px',
    color: '#fff',
  },
  variants: {
    checked: {
      true: {
        '&::before': {
          background: C.green,
          boxShadow: `inset 0 0 0 1px ${C.green}`,
        },
      },
    },
  },
});

const Empty = styled('p', {
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px 12px',
  margin: 0,
  fontSize: '14px',
  color: C.muted,
});

const Chips = styled('ul', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

const Chip = styled('li', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  maxWidth: '100%',
  minHeight: '28px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'rgba(231,244,230,0.5)',
  boxShadow: 'inset 0 0 0 1px rgb(130,205,122)',
  fontSize: '14px',
  lineHeight: '20px',
  '& > span': { minWidth: 0 },
  variants: {
    // BR-06: amber, like the calendar's deactivated-manager marker.
    inactive: {
      true: {
        background: 'rgba(232,163,23,0.08)',
        boxShadow: `inset 0 0 0 1px ${C.amber}`,
      },
    },
  },
});

const Note = styled('span', { color: C.muted });

const RosterError = styled('p', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
  color: C.red,
});

const Remove = styled('button', {
  display: 'inline-flex',
  flexShrink: 0,
  margin: 0,
  padding: 0,
  border: 'none',
  // The focus ring follows a shape.
  borderRadius: '4px',
  background: 'none',
  color: C.ink,
  cursor: 'pointer',
  '& svg': { width: '16px', height: '16px' },
  '&:focus-visible': { outline: `2px solid ${C.darkGreen}` },
  '&:disabled': { cursor: 'not-allowed' },
});

type Props = {
  /** Test-id stem: "ops" on the page, "opsRepeat" in the Repeat modal. */
  idPrefix: string;
  /** Active managers; undefined while loading. */
  roster?: OpsManager[];
  rosterError?: boolean;
  /** Refetch the roster after a load error. */
  onRetry?: () => void;
  /** The block's own managers, so a deactivated one keeps its name. */
  assigned?: OpsManagerSummary[];
  /** Applied managers, in chip order. */
  selectedIds: number[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  layer?: 'panel' | 'modal';
  /**
   * The empty roster's "Manage managers" link; none without it. Gets
   * this field's trigger, where focus returns once the roster closes.
   */
  onManageManagers?: (trigger: HTMLElement | null) => void;
};

const ManagerMultiSelect = ({
  idPrefix: p,
  roster,
  rosterError = false,
  onRetry,
  assigned = [],
  selectedIds,
  open,
  onToggle,
  onClose,
  onChange,
  disabled = false,
  layer,
  onManageManagers,
}: Props) => {
  // Opening copies the applied list into the draft; rows edit the draft
  // only. Apply commits it; any other close discards it.
  const [draft, setDraft] = useState<number[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  useDismiss(anchorRef, open, (reason) => {
    onClose();
    if (reason === 'escape') triggerRef.current?.focus();
  });

  // Opening moves focus into the dropdown, on the first manager.
  useLayoutEffect(() => {
    if (open) firstOptionRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Tabbing (or any focus move) out of the trigger and dropdown discards
  // the draft, like an outside click.
  const onBlur = (ev: FocusEvent<HTMLDivElement>) => {
    const next = ev.relatedTarget as Node | null;
    if (open && next && !anchorRef.current?.contains(next)) onClose();
  };

  const loading = roster === undefined && !rosterError;
  const options = roster ?? [];
  const rosterIds = new Set(options.map((m) => m.id));
  const names = new Map<number, OpsManagerSummary>();
  assigned.forEach((m) => names.set(m.id, m));
  options.forEach((m) => names.set(m.id, m));

  // Applied managers the roster no longer lists (deactivated). They show
  // as ticked, flagged rows, so the list agrees with "N selected"; unticking
  // one drops it on Apply, and once dropped it is gone from the list.
  const inactiveIds = roster
    ? selectedIds.filter((id) => !rosterIds.has(id))
    : [];

  const toggleDraft = (id: number) =>
    setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const apply = () => {
    // Deactivated managers still ticked keep their place first; the rest
    // follow the list order.
    onChange([
      ...inactiveIds.filter((id) => draft.includes(id)),
      ...options.filter((m) => draft.includes(m.id)).map((m) => m.id),
    ]);
    onClose();
    triggerRef.current?.focus();
  };

  // An unloadable or empty roster leaves the placeholder; the
  // dropdown or the error line below says why, once.
  let triggerText = 'Select managers';
  if (loading) triggerText = 'Loading managers…';
  else if (selectedIds.length > 0)
    triggerText = `${selectedIds.length} selected`;
  const rowCount = inactiveIds.length + options.length;

  const labelId = `${p}ManagerLabel`;
  const dropdownId = `${p}ManagerDropdown`;

  return (
    <Column>
      <Wrap>
        <FieldLabel id={labelId}>Operations manager</FieldLabel>
        <Anchor ref={anchorRef} onBlur={onBlur}>
          <Trigger
            ref={triggerRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? dropdownId : undefined}
            aria-labelledby={labelId}
            aria-describedby={`${p}ManagerValue`}
            placeholder={selectedIds.length === 0}
            // A failed refetch over cached data leaves the list usable;
            // only a roster that never loaded locks the trigger.
            disabled={disabled || loading || (rosterError && !roster)}
            data-testid={`${p}ManagerField`}
            onClick={(ev) => {
              // Keep the opening click from reaching any window clickout.
              ev.stopPropagation();
              if (!open) setDraft(selectedIds);
              onToggle();
            }}
          >
            <span id={`${p}ManagerValue`}>{triggerText}</span>
            <ChevronDown />
          </Trigger>
          {open && (
            <Dropdown
              id={dropdownId}
              role="dialog"
              aria-labelledby={labelId}
              // Clicks inside must not move focus first (no blur mid-click).
              onMouseDown={(ev) => ev.preventDefault()}
              layer={layer}
              data-testid={`${p}ManagerDropdown`}
            >
              {rowCount === 0 ? (
                <Empty data-testid={`${p}ManagerEmpty`}>
                  {rosterError ? MANAGERS_LOAD_ERROR : NO_MANAGERS}
                  {!rosterError && onManageManagers && (
                    <LinkButton
                      ref={firstOptionRef}
                      type="button"
                      data-testid={`${p}ManagerManageLink`}
                      onClick={() => onManageManagers(triggerRef.current)}
                    >
                      Manage managers
                    </LinkButton>
                  )}
                </Empty>
              ) : (
                <>
                  <Options>
                    {inactiveIds.map((id, i) => {
                      const checked = draft.includes(id);
                      const name =
                        names.get(id)?.full_name ?? 'Unknown manager';
                      return (
                        <Option
                          key={id}
                          ref={i === 0 ? firstOptionRef : undefined}
                          type="button"
                          role="checkbox"
                          aria-checked={checked}
                          aria-disabled={!checked || undefined}
                          aria-label={`${name} (inactive)`}
                          data-inactive="true"
                          data-testid={`${p}ManagerOption-${id}`}
                          onClick={() => {
                            if (checked) toggleDraft(id);
                          }}
                        >
                          <Box checked={checked} aria-hidden="true">
                            {checked && <CheckIcon />}
                          </Box>
                          <span>
                            {name} <InactiveNote>(inactive)</InactiveNote>
                          </span>
                        </Option>
                      );
                    })}
                    {options.map((m, i) => {
                      const checked = draft.includes(m.id);
                      return (
                        <Option
                          key={m.id}
                          ref={
                            i === 0 && inactiveIds.length === 0
                              ? firstOptionRef
                              : undefined
                          }
                          type="button"
                          role="checkbox"
                          aria-checked={checked}
                          data-testid={`${p}ManagerOption-${m.id}`}
                          onClick={() => toggleDraft(m.id)}
                        >
                          <Box checked={checked} aria-hidden="true">
                            {checked && <CheckIcon />}
                          </Box>
                          <span>{m.full_name}</span>
                        </Option>
                      );
                    })}
                  </Options>
                  <PrimaryButton
                    type="button"
                    data-testid={`${p}ManagerApplyButton`}
                    onClick={apply}
                  >
                    Apply selection
                  </PrimaryButton>
                </>
              )}
            </Dropdown>
          )}
        </Anchor>
      </Wrap>
      {rosterError && (
        <RosterError role="alert" data-testid={`${p}ManagerLoadError`}>
          {MANAGERS_LOAD_ERROR}
          {onRetry && (
            <LinkButton
              type="button"
              data-testid={`${p}ManagerRetryButton`}
              onClick={onRetry}
            >
              Retry
            </LinkButton>
          )}
        </RosterError>
      )}
      {selectedIds.length > 0 && (
        <Chips aria-label="Selected managers">
          {selectedIds.map((id) => {
            const name = names.get(id)?.full_name ?? 'Unknown manager';
            const inactive = isInactiveManager(id, roster, assigned);
            return (
              <Chip
                key={id}
                inactive={inactive}
                data-inactive={inactive || undefined}
                data-testid={`${p}ManagerChip-${id}`}
              >
                <span>
                  {name}
                  {inactive && <Note> {INACTIVE_CHIP_NOTE}</Note>}
                </span>
                <Remove
                  type="button"
                  aria-label={`Remove ${name}`}
                  disabled={disabled}
                  data-testid={`${p}ManagerChipRemove-${id}`}
                  onClick={() => onChange(selectedIds.filter((x) => x !== id))}
                >
                  <XMarkIcon />
                </Remove>
              </Chip>
            );
          })}
        </Chips>
      )}
    </Column>
  );
};

export default ManagerMultiSelect;
